import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowUpCircle, MessageSquareMore, HelpCircle, UserCheck, Ban, X, Send, Clock, User, Building2, Wallet, Tag, Check } from "lucide-react";
import { waeAPI, STATUS_STYLE, STATUS_LABEL, PRIORITY_STYLE, fmtDT, fmtDuration } from "./waeApi";
import { cls, Fld } from "./uiKit";

const ACTIONS = [
  { k: "approve",       label: "Approve",        icon: CheckCircle2,      tone: "bg-emerald-600 text-white hover:bg-emerald-700",  needsReason: false, actor:"approver" },
  { k: "reject",        label: "Reject",         icon: XCircle,           tone: "bg-rose-600 text-white hover:bg-rose-700",         needsReason: true,  actor:"approver" },
  { k: "send_back",     label: "Send Back",      icon: X,                 tone: "bg-orange-500 text-white hover:bg-orange-600",     needsReason: true,  actor:"approver" },
  { k: "request_info",  label: "Request Info",   icon: HelpCircle,        tone: "bg-violet-500 text-white hover:bg-violet-600",     needsReason: true,  actor:"approver" },
  { k: "delegate",      label: "Delegate",       icon: UserCheck,         tone: "bg-cyan-600 text-white hover:bg-cyan-700",         needsReason: false, needsUser: true, actor:"approver" },
  { k: "escalate",      label: "Escalate",       icon: ArrowUpCircle,     tone: "bg-amber-500 text-white hover:bg-amber-600",       needsReason: false, actor:"approver" },
  { k: "cancel",        label: "Cancel Request", icon: Ban,               tone: "bg-slate-500 text-white hover:bg-slate-600",       needsReason: true,  actor:"requester" },
];

export default function RequestDrawer({ requestId, currentUserId, onClose, onChanged }) {
  const [r, setR] = useState(null);
  const [ask, setAsk] = useState(null);       // {action, needsReason, needsUser}
  const [reason, setReason] = useState("");
  const [target, setTarget] = useState("");
  const [directory, setDirectory] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => waeAPI.request(requestId).then(setR).catch(()=>{});
  useEffect(() => { load(); }, [requestId]);   // eslint-disable-line
  useEffect(() => {
    if (ask?.needsUser) waeAPI.directory("").then(setDirectory);
  }, [ask]);

  if (!r) return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 text-[13px] text-slate-500">Loading…</div>
    </div>
  );

  const curLevelIdx = r.current_level_index;
  const curLevel = (r.levels||[])[curLevelIdx];
  const iAmRequester = r.requester_id === currentUserId;
  const iAmApprover = curLevel && (curLevel.resolved_approvers||[]).some(a => a.user_id === currentUserId);

  const submitAction = async () => {
    if (ask.needsReason && !reason.trim()) { toast.error(`Reason required for ${ask.k}`); return; }
    if (ask.needsUser && !target) { toast.error("Pick a delegate"); return; }
    setSaving(true);
    try {
      await waeAPI.action(r.id, {
        action: ask.k,
        comment: reason.trim() || undefined,
        delegate_to: target || undefined,
      });
      toast.success(`Request ${ask.label.toLowerCase()}d`);
      setAsk(null); setReason(""); setTarget("");
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const postComment = async () => {
    if (!commentBody.trim()) return;
    try {
      await waeAPI.comment(r.id, commentBody.trim());
      setCommentBody("");
      await load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-slate-50 w-full max-w-3xl shadow-2xl h-full flex flex-col"
           onClick={e=>e.stopPropagation()} data-testid={`wae-drawer-${r.id}`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="mono text-[12px] text-slate-500">{r.code}</span>
              <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium border ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-700"}`}
                    data-testid="wae-drawer-status">{STATUS_LABEL[r.status] || r.status}</span>
              {r.priority && r.priority !== "normal" && (
                <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium capitalize ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</span>
              )}
            </div>
            <h2 className="font-display text-[18px] font-bold text-hocp-navy mt-1">{r.subject}</h2>
            <div className="text-[12px] text-slate-500 mt-0.5">
              {r.approval_type_name} · {r.template_name}
            </div>
          </div>
          <button onClick={onClose} data-testid="wae-drawer-close"
                  className="ml-auto p-2 rounded-lg hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500"/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Meta grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetaCard icon={User}      label="Requester"  value={r.requester_name || r.requester_email}/>
            <MetaCard icon={Building2} label="Department" value={r.department_id ? "Dept" : "—"}/>
            <MetaCard icon={Wallet}    label="Amount"     value={r.amount != null ? `${r.currency||"INR"} ${Number(r.amount).toLocaleString("en-IN")}` : "—"}/>
            <MetaCard icon={Tag}       label="Entity"     value={r.entity_ref || r.entity_kind || "—"}/>
          </div>

          {r.description && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Description</div>
              <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{r.description}</div>
            </div>
          )}

          {r.meta && Object.keys(r.meta).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="wae-drawer-meta">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Metadata</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[12px]">
                {Object.entries(r.meta).map(([k, v]) => (
                  <div key={k}><span className="text-slate-400">{k}:</span> <span className="text-slate-700 mono">{String(v)}</span></div>
                ))}
              </div>
            </div>
          )}

          {/* Approval Chain */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="wae-drawer-chain">
            <div className="px-4 py-3 border-b border-slate-100 font-display text-[13px] font-semibold text-hocp-navy">
              Approval Chain
            </div>
            <ol className="divide-y divide-slate-100">
              {(r.levels || []).map((lv, i) => (
                <li key={lv.key} data-testid={`wae-level-${i}`}
                    className={`px-4 py-3 flex items-start gap-3 ${i===curLevelIdx ? "bg-sky-50/40" : ""}`}>
                  <StateBadge state={lv.state} idx={i} active={i===curLevelIdx}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-hocp-navy">{lv.name}</div>
                    <div className="text-[11.5px] text-slate-500 mt-0.5">
                      {lv.mode === "parallel" ? "Parallel · " : "Sequential · "}
                      {lv.quorum === "all" ? "All approvers" : "Any approver"}
                      {lv.sla_hours != null && ` · SLA ${lv.sla_hours}h`}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {(lv.resolved_approvers || []).slice(0, 6).map(a => (
                        <span key={a.user_id} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px]">
                          <span className="w-4 h-4 rounded-full bg-hocp-navy text-white text-[8px] flex items-center justify-center uppercase">
                            {(a.name||a.email||"?").charAt(0)}
                          </span>
                          {a.name || a.email}
                        </span>
                      ))}
                      {(lv.resolved_approvers||[]).length === 0 && lv.state !== "skipped" && (
                        <span className="text-[11px] text-slate-400 italic">No approver resolved</span>
                      )}
                      {lv.state === "skipped" && <span className="text-[11px] text-slate-400 italic">Skipped by condition</span>}
                    </div>
                    {/* Level action history */}
                    {(lv.approvals || []).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {lv.approvals.map((a, j) => (
                          <div key={j} className="text-[11.5px] text-slate-500">
                            <span className="font-medium text-hocp-navy capitalize">{a.action.replace("_"," ")}</span>
                            {" "}by <span className="mono">{a.actor_email}</span>
                            <span className="text-slate-400"> · {fmtDT(a.at)}</span>
                            {a.comment && <div className="text-slate-500 italic pl-3">&quot;{a.comment}&quot;</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-[11px] text-slate-400 shrink-0">
                    {lv.sla_due_at && <div>Due {fmtDT(lv.sla_due_at)}</div>}
                  </div>
                </li>
              ))}
              {r.status === "approved" && r.completed_at && (
                <li className="px-4 py-3 flex items-center gap-3 bg-emerald-50/40">
                  <StateBadge state="approved" idx="done"/>
                  <div className="text-[13px] font-medium text-emerald-800">Request Fully Approved</div>
                  <div className="ml-auto text-[11px] text-slate-500 mono">{fmtDT(r.completed_at)}</div>
                </li>
              )}
            </ol>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="wae-drawer-timeline">
            <div className="px-4 py-3 border-b border-slate-100 font-display text-[13px] font-semibold text-hocp-navy">
              Timeline
            </div>
            <ol className="divide-y divide-slate-100">
              {(r.timeline || []).map((e) => (
                <li key={e.id} className="px-4 py-2.5 flex items-start gap-3 text-[12px]">
                  <Clock className="w-3.5 h-3.5 text-slate-400 mt-0.5"/>
                  <div className="flex-1">
                    <div className="text-slate-700"><span className="capitalize font-medium">{e.action.replace(/_/g," ")}</span> {e.actor_email && <span className="text-slate-400">by <span className="mono">{e.actor_email}</span></span>}</div>
                    {e.meta?.reason && <div className="text-slate-500 italic text-[11.5px]">&quot;{e.meta.reason}&quot;</div>}
                  </div>
                  <div className="text-[11px] mono text-slate-400 shrink-0">{fmtDT(e.at)}</div>
                </li>
              ))}
              {!(r.timeline||[]).length && <div className="p-6 text-center text-[12px] text-slate-400">No events yet.</div>}
            </ol>
          </div>

          {/* Comments */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="wae-drawer-comments">
            <div className="px-4 py-3 border-b border-slate-100 font-display text-[13px] font-semibold text-hocp-navy flex items-center gap-2">
              <MessageSquareMore className="w-4 h-4"/> Comments
              <span className="text-[11px] text-slate-400 mono">({(r.comments||[]).length})</span>
            </div>
            <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
              {(r.comments || []).map(c => (
                <div key={c.id} className="px-4 py-2.5">
                  <div className="text-[11.5px] text-slate-500">
                    <span className="mono font-medium text-hocp-navy">{c.actor_email}</span>
                    <span className="text-slate-400"> · {fmtDT(c.at)}</span>
                  </div>
                  <div className="text-[13px] text-slate-700 mt-1">{c.body}</div>
                </div>
              ))}
              {!(r.comments||[]).length && <div className="p-4 text-center text-[12px] text-slate-400">No comments yet.</div>}
            </div>
            <div className="p-3 border-t border-slate-100 bg-slate-50 flex gap-2">
              <input value={commentBody} onChange={e=>setCommentBody(e.target.value)}
                     data-testid="wae-comment-input" placeholder="Add a comment…"
                     onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postComment(); }}}
                     className={cls + " flex-1"}/>
              <button onClick={postComment} disabled={!commentBody.trim()} data-testid="wae-comment-send"
                      className="h-10 px-3 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-1.5">
                <Send className="w-3.5 h-3.5"/> Send
              </button>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="border-t border-slate-200 bg-white px-6 py-3 flex flex-wrap gap-2" data-testid="wae-drawer-actions">
          {ACTIONS.filter(a => {
            if (r.status !== "pending" && r.status !== "escalated" && r.status !== "info_requested") return false;
            if (a.actor === "requester" && !iAmRequester) return false;
            if (a.actor === "approver" && !iAmApprover) return false;
            return true;
          }).map(a => {
            const I = a.icon;
            return (
              <button key={a.k} onClick={()=>{ setAsk(a); setReason(""); setTarget(""); }}
                      data-testid={`wae-action-${a.k}`}
                      className={`h-9 px-3 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 ${a.tone}`}>
                <I className="w-3.5 h-3.5"/> {a.label}
              </button>
            );
          })}
          {!iAmApprover && !iAmRequester && r.status === "pending" && (
            <div className="text-[12px] text-slate-500 italic">You are not an approver on the current level.</div>
          )}
        </div>

        {/* Action dialog inline */}
        {ask && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={()=>setAsk(null)}>
            <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3"
                 onClick={e=>e.stopPropagation()} data-testid={`wae-action-dialog-${ask.k}`}>
              <h3 className="font-display text-[16px] font-semibold text-hocp-navy">{ask.label}</h3>
              {ask.needsUser && (
                <Fld label="Delegate to">
                  <select value={target} onChange={e=>setTarget(e.target.value)}
                          data-testid="wae-action-target" className={cls}>
                    <option value="">Select…</option>
                    {directory.map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.name} — {u.email}</option>
                    ))}
                  </select>
                </Fld>
              )}
              <Fld label={ask.needsReason ? "Reason (required)" : "Comment (optional)"}>
                <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}
                          data-testid="wae-action-reason"
                          className={cls + " h-auto py-2 resize-y"}
                          placeholder={ask.k === "reject" ? "Explain the rejection…" : "Optional comment"}/>
              </Fld>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={()=>setAsk(null)} className="h-9 px-3 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
                <button onClick={submitAction} disabled={saving} data-testid={`wae-action-confirm-${ask.k}`}
                        className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium disabled:opacity-60">
                  {saving ? "Saving…" : ask.label}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MetaCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">
        <Icon className="w-3 h-3"/> {label}
      </div>
      <div className="mt-1 text-[13px] font-medium text-hocp-navy truncate">{value}</div>
    </div>
  );
}

function StateBadge({ state, idx, active }) {
  const map = {
    pending:   "bg-sky-100 text-sky-700",
    approved:  "bg-emerald-100 text-emerald-700",
    rejected:  "bg-rose-100 text-rose-700",
    escalated: "bg-amber-100 text-amber-800",
    skipped:   "bg-slate-100 text-slate-400",
  };
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0 ${map[state] || "bg-slate-100 text-slate-500"} ${active ? "ring-2 ring-hocp-gold" : ""}`}>
      {idx === "done" || state === "approved" ? <Check size={12} strokeWidth={2.5} /> : state === "rejected" ? <X size={12} strokeWidth={2.5} /> : (typeof idx === "number" ? idx + 1 : "•")}
    </div>
  );
}
